import { useAsync } from "@bezoned/shared/hooks/use-async.js";
import { getTeamsBeZonedDeepLink, routes } from "@bezoned/shared/routes.js";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect } from "react";

import { useAuth } from "../features/auth/use-auth.js";
import { TeamInstallationContent } from "../features/onboarding/team/team-installation-content.js";
import { usePlatform } from "../features/platform-detection/use-platform.js";
import {
  appInfoGet,
  teamInstallationStatusGet,
} from "../services/api.service.js";
import { recordErrorInSentry } from "../utils/init-sentry.js";

export const TeamInstallationPage = () => {
  const { user } = useAuth();
  const { getPlatformContext } = usePlatform();

  const getTeamIdFromMsTeamsContext = useCallback(async () => {
    const context = await getPlatformContext();
    return context?.team?.groupId ?? null;
  }, [getPlatformContext]);

  const teamIdQuery = useAsync(getTeamIdFromMsTeamsContext);

  const teamInstallationStatusQuery = useQuery({
    enabled: Boolean(teamIdQuery.data),
    queryFn: () => teamInstallationStatusGet(teamIdQuery.data ?? ""),
    queryKey: ["teamInstallationStatus", teamIdQuery.data],
  });

  useEffect(() => {
    if (teamIdQuery.error) {
      recordErrorInSentry(teamIdQuery.error);
    }
  }, [teamIdQuery.error]);

  const isNewTenant = teamInstallationStatusQuery.data?.state === "new_tenant";
  const teamsAppQuery = useQuery({
    enabled: isNewTenant,
    queryFn: () => appInfoGet(),
    queryKey: ["appInfo", user.tenantId],
  });
  const teamsAppId = teamsAppQuery.data?.teamsAppId;

  const lobbyUrl =
    isNewTenant && teamsAppId
      ? getTeamsBeZonedDeepLink(teamsAppId, "msteams")
      : routes.protected.chooseTeam;

  useEffect(() => {
    if (teamInstallationStatusQuery.error) {
      recordErrorInSentry(teamInstallationStatusQuery.error);
    }
  }, [teamInstallationStatusQuery.error]);

  useEffect(() => {
    if (teamsAppQuery.error) {
      recordErrorInSentry(teamsAppQuery.error);
    }
  }, [teamsAppQuery.error]);

  return (
    <TeamInstallationContent
      loading={
        teamIdQuery.isLoading ||
        teamInstallationStatusQuery.isLoading ||
        (isNewTenant && teamsAppQuery.isLoading)
      }
      msTeamId={teamIdQuery.data}
      redirect={{
        openInNewTab: isNewTenant,
        url: lobbyUrl,
      }}
      installationState={teamInstallationStatusQuery.data?.state}
      error={
        teamInstallationStatusQuery.error ||
        (isNewTenant && teamsAppQuery.error) ||
        (teamIdQuery.error as Error) ||
        null
      }
    />
  );
};
